import { PageLayout } from '@/layouts/page-layout'
import { GroupInput } from '@/components/v2/group-input'
import { Button } from '@material-tailwind/react'
import { Link, useLoaderData, useNavigation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Form, Formik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { CustomYup, toOptions } from '@/utils'
import { EmployeeInformationForm } from '../shared/forms'
import PropTypes from 'prop-types'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'
import { list } from '@/router/utils'
import { useCreateEmployeeMutation, useDeleteEmployeeMutation } from '@/store/services'

const validationSchema = CustomYup.object().shape({
  has_local_document_number: CustomYup.boolean(),
  document_number: CustomYup.string()
    .rut()
    .when('has_local_document_number', {
      is: false,
      then: () => CustomYup.string()
    }),
  name: CustomYup.string().required('Nombre es requerido'),
  last_name: CustomYup.string().required('Apellido es requerido'),
  birth_date: CustomYup.string().required('Fecha de nacimiento es requerida'),
  country_id: CustomYup.number().required('País es requerido')
})

export const EmployeeCreate = ({ editMode }) => {
  const { state } = useNavigation()
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState(null)

  const loaderData = useLoaderData()
  const countriesData = loaderData.countriesData
  const [createEmployee] = useCreateEmployeeMutation()
  const [deleteEmployee] = useDeleteEmployeeMutation()

  const countries = countriesData?.data?.countries || []
const initialValues = {
  name: '',
  last_name: '',
  document_number: '',
  birth_date: '',
  country_id: null,
  gender: 'male',
  disability: false,
  pensioner: false,
  has_local_document_number: true,

  email: '',
  phone: '',
  address: '',
  region_id: '',
  marital_status: '',

  address_attributes: {
    street: '',
    state_id: '',
  }
}

  const countryOptions = useMemo(() => {
    return toOptions({
      data: countries,
      value: 'id',
      label: 'name'
    });
  }, [countries]);

  const onSubmit = async (values) => {
  try {
    const payload = {
      ...values,
      address: values?.address_attributes?.street || '',
      region_id: values?.address_attributes?.state_id || '',
    }

    const response = await fetch('/api/v1/employees/temp_create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee: payload })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw errorData.errors
    }

    const { temp_hash } = await response.json()
    navigate(`/employees/new?temp_hash=${temp_hash}`)

  } catch (error) {
    const errors = Array.isArray(error) ? error : [error.message || t('errors.temporary_creation_failed')]
    setSubmitError(errors)
  }
}

  return (
    <PageLayout
      editMode={editMode}
      title={t('actions.create.with', { what: t('employee.one') })}
    >
      {submitError && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {submitError.length === 1 ? (
            submitError[0]
          ) : (
            <ul className="list-disc pl-5">
              {submitError.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validateOnMount={false}
      >
        {({ handleSubmit, isSubmitting, isValidating, touched }) => (
          <Form>
            <GroupInput
              title={t('employee.one')}
              description={t('new.employee.section.personalInformation')}
              className='py-8'
              divider={false}
            >
              <EmployeeInformationForm 
                countryOptions={countryOptions} 
              />
            </GroupInput>

            <div className='flex justify-end gap-3 mt-10'>
              <HasAccess permissions='employee#create'>
                <Button 
                  loading={state === 'submitting' || isSubmitting}
                  type='submit'
                  disabled={isSubmitting}
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {t('actions.next')}
                </Button>
              </HasAccess>
              <Link to={list('employees')}>
                <Button variant='text' type='button'>
                  {t('actions.cancel')}
                </Button>
              </Link>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}

EmployeeCreate.propTypes = {
  editMode: PropTypes.bool
}
